import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from "react-i18next";
import {Box} from '@mui/material';
import {useSearchParams} from "react-router-dom";
import {addToUrlParams} from "@dreebit-registry/vsm-microservice-components";
import {GridPaginationModel} from "@mui/x-data-grid-pro";
import CreateInstalledBaseEventModal from "../CreateInstalledBaseEventModal";
import EventListTable, {EventNavigation} from "./table";
import {Direction, ManualEventType} from "../../codegen/graphql";
import {Button} from "@mui/material";
import {AddRounded} from "@mui-symbols-material/w300";
import {InstalledBaseEventListFilterTDAs} from "../../graphql/queries/installedBaseEventListFilterTDAs";
import _ from "lodash";
import {withoutGlobalOnlyFilters} from "../../utils/assetEventListFilters";
import EventListExportButton from "../EventListExportButton";
import ListFilter from "../ListFilter";
import {useQuery} from "@apollo/client";
import {InstalledBaseEventListFilter} from "../../codegen/graphql";
import getFilter from "../../utils/getFilter";
import {withoutChangelogFilterOptions, withVisibleEventCategories} from "../../utils/eventListFilter";
import {useResizeObserver} from "../../hooks/useResizeObserver";


interface Props {
    assetId: string,
    assetLocationId: string | null
    installedBaseNodeId: string | null
    onOpenEventDetailView: (id: string | number, options?: { replace?: boolean }) => void
    onEditEvent?: (id: string | number) => void
    selectedEventId: string | number | null
    onNavigationChange?: (navigation: EventNavigation | null) => void
}


const AssetEventList = ({assetId, assetLocationId, installedBaseNodeId, onOpenEventDetailView, onEditEvent, selectedEventId, onNavigationChange}: Props) => {
    const {t} = useTranslation();
    const [ref, filterHeight] = useResizeObserver();
    // Read URL params synchronously for auto-opening create event modal
    const [initialEventType] = useState<ManualEventType | undefined>(() => {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get('eventType');
        return raw && (Object.values(ManualEventType) as string[]).includes(raw)
            ? (raw as ManualEventType)
            : undefined;
    });
    const [createEventModalOpen, setCreateEventModalOpen] = React.useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('createEvent') === 'true';
    });
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

    const showEnvBanner = process.env.REACT_APP_SHOW_TESTING_BANNER === "true"
    const tableHeight = viewportHeight
        - (showEnvBanner ? 40 : 0) // Test Env Banner
        - filterHeight
        - 63 // Header
        - 50 // Tabs
        - 24 // Breadcrumbs
        - 70 //Toolbar-Box


    const [urlParams, setUrlParams] = useSearchParams();

    // Clean up createEvent URL params after reading
    useEffect(() => {
        if (urlParams.get('createEvent')) {
            const newParams = new URLSearchParams(urlParams);
            newParams.delete('createEvent');
            newParams.delete('eventType');
            setUrlParams(newParams, { replace: true });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleResize = () => {
            setViewportHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [searchParams, setSearchParams] = useState({
        query: '',
        fields: ['name']
    })

    const onDebouncedSearch = _.debounce((values) => {
        setSearchParams(values);
    }, 700);



    const { data } = useQuery(InstalledBaseEventListFilterTDAs, {
        variables: {
            typeDefinitionIndex: "INSTALLED_BASE_EVENT_LIST_FILTER",
            options: {
                assetId: assetId,
            }
        },
        skip: !assetId
    });

    const [userFilter, setUserFilter] = useState<InstalledBaseEventListFilter>({});

    // Memoized: the table refetches on every new filter object, and the detail panel's
    // navigation re-renders this component on each load — a fresh object here loops.
    const finalFilter = useMemo(() => withVisibleEventCategories({
        ...userFilter,
        assetId: { equals: assetId },
        installedBaseNodeId: installedBaseNodeId ? { equals: installedBaseNodeId } : undefined
    }), [userFilter, assetId, installedBaseNodeId]);

    const attributes = useMemo(
        () => withoutChangelogFilterOptions(data?.installedBaseEventList?.typeDefinitionAttributeList?.typeDefinitionAttributes || []),
        [data]
    );

    // The history only covers the current installation (filtered by its node), so an
    // unfiltered empty list usually means the asset was reinstalled recently.
    const isUnfiltered = !searchParams.query && Object.keys(userFilter).length === 0;


    return (
        <>

                <Box ref={ref} sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '8px'}}>
                    <ListFilter
                        attributes={withoutGlobalOnlyFilters(attributes)}
                        onSearch={onDebouncedSearch}
                        searchParams={searchParams}
                        initialValues={{}}
                        onValuesChange={(values) => {
                            const formattedFilters: Record<string, any> = {};
                            const eventType = values['eventType'];
                            if (eventType) {
                                if (Array.isArray(eventType) && eventType.length === 0) {
                                } else {
                                    formattedFilters['eventType'] = getFilter(eventType, "in");
                                }
                            }
                            const eventCategory = values['eventCategory'];
                            if (eventCategory) {
                                if (Array.isArray(eventCategory) && eventCategory.length === 0) {
                                } else {
                                    formattedFilters['eventCategory'] = getFilter(eventCategory, "in");
                                }
                            }
                            const createdAt = values['createdAt'];
                            if (createdAt) {
                                formattedFilters['createdAt'] = createdAt;
                            }
                            setUserFilter(formattedFilters);
                        }}
                        onReset={() => {
                            setUserFilter({});
                            setSearchParams({ query: '', fields: ['name'] });
                        }}
                        formStyle={{paddingTop: '0px'}}
                    />
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '13px'}}>
                        <Button
                            variant="contained"
                            startIcon={<AddRounded/>}
                            onClick={() => setCreateEventModalOpen(true)}
                            sx={{
                                color:'#333333',
                                backgroundColor: '#FDDDCD',
                                '&:hover': {
                                    backgroundColor: '#FFBA99',
                                }
                            }}
                        >
                            {t('Create')}
                        </Button>

                        <EventListExportButton
                        searchParams={searchParams}
                        filter= {finalFilter}
                        />

                    </Box>
                </Box>

            <EventListTable
                height={tableHeight}
                urlParams={urlParams}
                filter={finalFilter}
                search={searchParams}
                sort={{ insert: Direction.desc }}
                onChangePagination={(newPaginationModel: GridPaginationModel) => {
                    if (newPaginationModel) {
                        addToUrlParams(urlParams, newPaginationModel, setUrlParams)
                    }
                }}
                onOpenEventDetailView={onOpenEventDetailView}
                onEditEvent={onEditEvent}
                selectedEventId={selectedEventId}
                onNavigationChange={onNavigationChange}
                noRowsLabel={isUnfiltered ? t('This asset has no events since its current installation.') : undefined}
            />
            {createEventModalOpen && <CreateInstalledBaseEventModal
                assetId={assetId}
                assetLocationId={assetLocationId ?? undefined}
                installedBaseNodeId={installedBaseNodeId}
                open={createEventModalOpen}
                onClose={() => setCreateEventModalOpen(false)}
                refetchQueries={["AssetBreadcrumbPath", "AssetNameplateInfo", "InstalledBaseEventList"]}
                initialEventType={initialEventType}
            />}
        </>
    );
}

export default AssetEventList;
